import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Truck, MapPin, Trash2, Pencil, Printer, Download, Settings2, Upload, ChevronDown } from 'lucide-react';
import { shippingApi } from '../../api/endpoints/shipping';
import { torodLabel, torodId } from '../../lib/formatters';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Toggle } from '../../components/ui/Toggle';
import { PermissionGate } from '../../components/ui/PermissionGate';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { Tabs } from '../../components/ui/Tabs';
import { useToast } from '../../components/ui/Alert';
import type { ShippingCarrier, ShippingAddress, CreateAddressInput, CarrierPricingMode, CartBasedPricingRule } from '../../types/shipping';

export function ShippingPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('carriers');

  // Address modal (single warehouse/pickup address, Torod cascading location picker)
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState<ShippingAddress | null>(null);
  const [addrName, setAddrName] = useState('');
  const [addrPhone, setAddrPhone] = useState('');
  const [addrStreet, setAddrStreet] = useState('');
  const [addrCountry, setAddrCountry] = useState<number | null>(null);
  const [addrRegion, setAddrRegion] = useState<number | null>(null);
  const [addrCity, setAddrCity] = useState<number | null>(null);
  const [addrDistrict, setAddrDistrict] = useState<number | null>(null);

  // Carrier pricing modal
  const [pricingCarrier, setPricingCarrier] = useState<ShippingCarrier | null>(null);
  const [pricingMode, setPricingMode] = useState<CarrierPricingMode>('original');
  const [carrierFee, setCarrierFee] = useState(0);
  const [carrierDiscount, setCarrierDiscount] = useState(0);
  const [discountMinOrder, setDiscountMinOrder] = useState(0);
  const [fixedPrice, setFixedPrice] = useState(0);
  const [priceDeduction, setPriceDeduction] = useState(0);
  const [freeThreshold, setFreeThreshold] = useState(0);
  const [showOriginalPrice, setShowOriginalPrice] = useState(false);
  const [cartRules, setCartRules] = useState<CartBasedPricingRule[]>([]);
  const [showAdvancedPricing, setShowAdvancedPricing] = useState(false);

  // Delete carrier state
  const [deleteCarrierId, setDeleteCarrierId] = useState<string | null>(null);

  // Custom carrier modal state
  const [showCustomCarrierModal, setShowCustomCarrierModal] = useState(false);
  const [customCarrierName, setCustomCarrierName] = useState('');
  const [customCarrierLogo, setCustomCarrierLogo] = useState('');
  const [customCarrierDelivery, setCustomCarrierDelivery] = useState('');
  const [customCarrierFee, setCustomCarrierFee] = useState(0);

  // Label generation state
  const [labelOrderId, setLabelOrderId] = useState('');
  const [labelCarrierId, setLabelCarrierId] = useState('');

  // ── Queries ──

  const { data: carrierData, isLoading: carriersLoading } = useQuery({
    queryKey: ['shipping-carriers', storeId],
    queryFn: () => shippingApi.listCarriers(storeId!),
    enabled: !!storeId,
  });

  const [_showDiscovery, _setShowDiscovery] = useState(false);

  const { data: availableData, isLoading: availableLoading } = useQuery({
    queryKey: ['shipping-available', storeId],
    queryFn: () => shippingApi.listAvailableCarriers(storeId!),
    enabled: !!storeId,
  });

  const addCarrierMutation = useMutation({
    mutationFn: (carrier: { id: number; title: string; title_arabic?: string; carrier_logo?: string }) =>
      shippingApi.addCarrier(storeId!, {
        carrierId: String(carrier.id),
        name: carrier.title,
        arName: carrier.title_arabic,
        carrier_logo: carrier.carrier_logo,
        gateway: 'torod',
        isActive: true,
      } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping-carriers', storeId] });
      toast('success', t('common.success'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const { data: addressData, isLoading: addressesLoading } = useQuery({
    queryKey: ['shipping-addresses', storeId],
    queryFn: () => shippingApi.listAddresses(storeId!),
    enabled: !!storeId,
  });

  // ── Carrier mutations ──

  const toggleCarrierMutation = useMutation({
    mutationFn: ({ carrierId, isActive }: { carrierId: string; isActive: boolean }) =>
      shippingApi.updateCarrier(storeId!, carrierId, { isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping-carriers', storeId] });
      toast('success', t('common.success'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const deleteCarrierMutation = useMutation({
    mutationFn: (carrierId: string) => shippingApi.deleteCarrier(storeId!, carrierId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping-carriers', storeId] });
      queryClient.invalidateQueries({ queryKey: ['shipping-available', storeId] });
      toast('success', t('common.success'));
      setDeleteCarrierId(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  const createCustomCarrierMutation = useMutation({
    mutationFn: (data: { name: string; carrier_logo?: string; deliveryEstimate?: string; baseFee?: number }) =>
      shippingApi.createCustomCarrier(storeId!, {
        carrierId: `custom_${Date.now()}`,
        name: data.name,
        gateway: 'custom',
        carrier_logo: data.carrier_logo,
        deliveryEstimate: data.deliveryEstimate,
        baseFee: data.baseFee,
        isActive: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping-carriers', storeId] });
      queryClient.invalidateQueries({ queryKey: ['shipping-available', storeId] });
      toast('success', t('common.success'));
      closeCustomCarrierModal();
    },
    onError: () => toast('error', t('common.error')),
  });

  const updateCarrierPricingMutation = useMutation({
    mutationFn: ({ carrierId, data }: { carrierId: string; data: Partial<ShippingCarrier> }) =>
      shippingApi.updateCarrier(storeId!, carrierId, data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping-carriers', storeId] });
      toast('success', t('common.success'));
      setPricingCarrier(null);
    },
    onError: () => toast('error', t('common.error')),
  });

  function openPricingModal(carrier: ShippingCarrier) {
    setPricingCarrier(carrier);
    setPricingMode(carrier.pricingMode ?? 'original');
    setCarrierFee(carrier.fee ?? 0);
    setCarrierDiscount(carrier.discount ?? 0);
    setDiscountMinOrder(carrier.discountMinimumOrderAmount ?? 0);
    setFixedPrice(carrier.fixedPrice ?? 0);
    setPriceDeduction(carrier.priceDeduction ?? 0);
    setFreeThreshold(carrier.freeShippingThreshold ?? 0);
    setShowOriginalPrice(carrier.showOriginalPrice ?? false);
    setCartRules(carrier.cartBasedPricing ?? []);
    setShowAdvancedPricing(false);
  }

  function savePricing() {
    if (!pricingCarrier) return;
    updateCarrierPricingMutation.mutate({
      carrierId: pricingCarrier.carrierId || pricingCarrier._id,
      data: {
        pricingMode,
        fee: carrierFee,
        discount: carrierDiscount,
        discountMinimumOrderAmount: discountMinOrder,
        fixedPrice,
        priceDeduction,
        freeShippingThreshold: freeThreshold || null,
        showOriginalPrice,
        cartBasedPricing: cartRules,
      } as any,
    });
  }

  function addCartRule() {
    setCartRules(prev => [...prev, { minCartValue: 0, maxCartValue: 0, shippingPrice: 0, freeShipping: false }]);
  }

  function updateCartRule(index: number, field: keyof CartBasedPricingRule, value: number | boolean) {
    setCartRules(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  }

  function removeCartRule(index: number) {
    setCartRules(prev => prev.filter((_, i) => i !== index));
  }

  function closeCustomCarrierModal() {
    setShowCustomCarrierModal(false);
    setCustomCarrierName('');
    setCustomCarrierLogo('');
    setCustomCarrierDelivery('');
    setCustomCarrierFee(0);
  }

  function handleCustomCarrierLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCustomCarrierLogo(reader.result as string);
    reader.readAsDataURL(file);
  }

  function submitCustomCarrier() {
    if (!customCarrierName.trim()) return;
    createCustomCarrierMutation.mutate({
      name: customCarrierName,
      carrier_logo: customCarrierLogo || undefined,
      deliveryEstimate: customCarrierDelivery || undefined,
      baseFee: customCarrierFee || undefined,
    });
  }

  // ── Address mutations ──

  const addAddressMutation = useMutation({
    mutationFn: (data: CreateAddressInput) => shippingApi.addAddress(storeId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping-addresses', storeId] });
      toast('success', t('common.success'));
      closeAddressModal();
    },
    onError: () => toast('error', t('common.error')),
  });

  function resetAddressForm() {
    setEditingAddress(null);
    setAddrName('');
    setAddrPhone('');
    setAddrStreet('');
    setAddrCountry(null);
    setAddrRegion(null);
    setAddrCity(null);
    setAddrDistrict(null);
  }

  function closeAddressModal() {
    setShowAddressModal(false);
    resetAddressForm();
  }

  function openAddAddress() {
    resetAddressForm();
    setShowAddressModal(true);
  }

  function openEditAddress(addr: ShippingAddress) {
    resetAddressForm();
    setEditingAddress(addr);
    setAddrName(addr.name || '');
    setAddrPhone(addr.phone || '');
    setShowAddressModal(true);
  }

  function submitAddress() {
    // name is always sent; location is only included when a city is (re)selected,
    // so editing just the contact info keeps the existing Torod warehouse location.
    const payload: CreateAddressInput = { name: addrName, phone: addrPhone };
    if (addrCity) {
      const cn = addrCountries?.find((c) => torodId(c, 'country') === addrCountry);
      const rn = addrRegions?.find((r) => torodId(r, 'region') === addrRegion);
      const ct = addrCities?.find((c) => torodId(c, 'city') === addrCity);
      const dt = addrDistricts?.find((d) => torodId(d, 'district') === addrDistrict);
      payload.country = addrCountry;
      payload.region = addrRegion;
      payload.city = addrCity;
      payload.district = addrDistrict;
      payload.countryName = torodLabel(cn, i18n.language);
      payload.regionName = torodLabel(rn, i18n.language);
      payload.cityName = torodLabel(ct, i18n.language);
      payload.districtName = torodLabel(dt, i18n.language);
      payload.address = [payload.cityName, payload.districtName, addrStreet].filter(Boolean).join(' ');
    } else if (addrStreet) {
      payload.address = addrStreet;
    }
    addAddressMutation.mutate(payload);
  }

  // New address requires a full location (Torod needs a city id); editing can
  // save name/phone only, keeping the existing location.
  const canSaveAddress = !!addrName && (!!editingAddress || !!addrCity);

  const deleteAddressMutation = useMutation({
    mutationFn: (addressId: string) => shippingApi.deleteAddress(storeId!, addressId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping-addresses', storeId] });
      toast('success', t('common.success'));
    },
    onError: () => toast('error', t('common.error')),
  });

  // ── Label mutation ──

  // Labels are served from the 00pays origin; the API may return a relative path.
  function normalizeLabelUrl(url?: string) {
    if (!url) return url || '';
    return url.startsWith('http') ? url : `https://00pays.com${url}`;
  }

  const printLabelMutation = useMutation({
    mutationFn: (orderId: string) => shippingApi.printLabel(storeId!, orderId),
    onSuccess: (data) => {
      toast('success', t('shippingRates.labelSuccess'));
      window.open(normalizeLabelUrl(data.labelUrl), '_blank');
      setLabelOrderId('');
    },
    onError: () => toast('error', t('common.error')),
  });

  const createLabelMutation = useMutation({
    mutationFn: ({ orderId, carrierId }: { orderId: string; carrierId: string }) =>
      shippingApi.createLabel(storeId!, { orderId, carrierId }),
    onSuccess: (data) => {
      toast('success', t('shippingLabels.created', 'Label created'));
      if (data.shippingLabel) window.open(normalizeLabelUrl(data.shippingLabel), '_blank');
      setLabelOrderId('');
      setLabelCarrierId('');
    },
    onError: () => toast('error', t('common.error')),
  });

  // ── Torod queries for the address modal (cascading country → district picker) ──

  const { data: addrCountries } = useQuery({
    queryKey: ['torod-countries', storeId],
    queryFn: () => shippingApi.getTorodCountries(storeId!),
    enabled: !!storeId && showAddressModal,
  });

  const { data: addrRegions } = useQuery({
    queryKey: ['torod-regions', storeId, addrCountry],
    queryFn: () => shippingApi.getTorodRegions(storeId!, addrCountry!),
    enabled: !!storeId && addrCountry !== null,
  });

  const { data: addrCities } = useQuery({
    queryKey: ['torod-cities', storeId, addrRegion],
    queryFn: () => shippingApi.getTorodCities(storeId!, addrRegion!),
    enabled: !!storeId && addrRegion !== null,
  });

  const { data: addrDistricts } = useQuery({
    queryKey: ['torod-districts', storeId, addrCity],
    queryFn: () => shippingApi.getTorodDistricts(storeId!, addrCity!),
    enabled: !!storeId && addrCity !== null,
  });

  const downloadLabelMutation = useMutation({
    mutationFn: (orderId: string) => shippingApi.downloadLabel(storeId!, orderId),
    onSuccess: () => {
      toast('success', t('shippingRates.labelDownloaded', 'Label downloaded'));
    },
    onError: () => toast('error', t('common.error')),
  });

  // ── Helpers ──

  const carriers = carrierData?.carriers ?? [];
  const addresses = addressData?.addresses ?? [];

  if (carriersLoading || addressesLoading) return <LoadingSpinner fullPage />;

  return (
    <div>
      <h1 className="text-xl font-bold mb-6">{t('sidebar.shipping')}</h1>
      <Tabs
        tabs={[
          { key: 'carriers', label: t('shipping.carriers'), icon: <Truck className="h-4 w-4" /> },
          { key: 'addresses', label: t('shipping.addresses'), icon: <MapPin className="h-4 w-4" /> },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
        className="mb-6"
      />

      {/* ── Carriers Tab ── */}
      {activeTab === 'carriers' && (
        <>
          {/* All available carriers - always visible, sorted by priority */}
          <div className="mb-6">
            <h2 className="text-sm font-medium text-text-muted uppercase tracking-wider mb-3">{t('shipping.availableCarriers', 'Available Carriers')}</h2>
            {availableLoading ? (
              <div className="py-8"><LoadingSpinner /></div>
            ) : !availableData?.companies?.length ? (
              <div className="rounded-xl border border-border-default bg-bg-secondary p-6 text-center">
                <Truck className="h-8 w-8 text-text-muted mx-auto mb-2" />
                <p className="text-sm text-text-muted">{t('shipping.noCarriersAvailable', 'No shipping companies available. Create an order first to see carriers with rates.')}</p>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {availableData.companies.map((c) => {
                  const alreadyAdded = (c as any).isAdded || carriers.some(existing => existing.carrierId === String(c.id));
                  return (
                    <div key={c.id} className={`rounded-xl border p-4 transition-colors ${alreadyAdded ? 'border-success/30 bg-success/5' : 'border-border-default bg-bg-secondary hover:border-accent/30'}`}>
                      <div className="flex items-start gap-3">
                        <div className="flex h-10 w-16 shrink-0 items-center justify-center rounded-lg bg-bg-tertiary overflow-hidden p-1">
                          {c.carrier_logo ? (
                            <img src={c.carrier_logo} alt="" className="h-full w-full object-contain" />
                          ) : (
                            <Truck className="h-5 w-5 text-text-muted" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-medium truncate">{c.title_arabic || c.title}</p>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-text-muted mt-0.5">
                            {c.rate != null && <span className="font-semibold text-accent">{c.rate.toFixed(2)} SAR</span>}
                            {c.approximate_delivery_time_arabic && <span>· {c.approximate_delivery_time_arabic}</span>}
                          </div>
                          {c.warning && <p className="text-xs text-warning mt-1">{c.warning}</p>}
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        {alreadyAdded ? (
                          <span className="text-xs font-medium text-success">{t('shipping.addedToStore', 'Added to your store')}</span>
                        ) : (
                          <span className="text-xs text-text-muted">{t('shipping.notAdded', 'Not added')}</span>
                        )}
                        <PermissionGate requires="shipping.any">
                          {alreadyAdded ? (() => {
                            const cName = (c.title || '').toLowerCase().replace(/[^a-z]/g, '');
                            const existing = carriers.find(e =>
                              e.carrierId === String(c.id) ||
                              String(e.refId) === String(c.id) ||
                              (cName && (e.title || e.name || '').toLowerCase().replace(/[^a-z]/g, '') === cName)
                            );
                            return (
                            <div className="flex items-center gap-2">
                              <Toggle
                                checked={existing?.isActive ?? true}
                                onChange={(v) => {
                                  if (existing) toggleCarrierMutation.mutate({ carrierId: existing.carrierId || existing._id, isActive: v });
                                }}
                              />
                              <Button size="sm" variant="secondary" onClick={() => {
                                if (existing) openPricingModal(existing);
                              }}>
                                <Settings2 className="h-3.5 w-3.5" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-text-muted hover:text-danger hover:bg-danger/10" onClick={() => {
                                if (existing) setDeleteCarrierId(existing.carrierId || existing._id);
                              }}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>);
                          })() : (
                            <Button
                              size="sm"
                              onClick={() => addCarrierMutation.mutate(c)}
                              isLoading={addCarrierMutation.isPending}
                            >
                              <Plus className="h-3.5 w-3.5" />
                              {t('common.add')}
                            </Button>
                          )}
                        </PermissionGate>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Add Custom Carrier button */}
          <div className="mb-6">
            <PermissionGate requires="shipping.any">
              <Button onClick={() => setShowCustomCarrierModal(true)}>
                <Plus className="h-4 w-4" />
                {t('shipping.addCustomCarrier', 'Add Custom Carrier')}
              </Button>
            </PermissionGate>
          </div>

          {/* Delete Carrier Confirmation Modal */}
          <Modal
            isOpen={!!deleteCarrierId}
            onClose={() => setDeleteCarrierId(null)}
            title={t('common.confirm')}
            size="sm"
            footer={
              <>
                <Button variant="secondary" onClick={() => setDeleteCarrierId(null)}>{t('common.cancel')}</Button>
                <Button
                  variant="danger"
                  onClick={() => deleteCarrierId && deleteCarrierMutation.mutate(deleteCarrierId)}
                  isLoading={deleteCarrierMutation.isPending}
                >
                  {t('common.delete')}
                </Button>
              </>
            }
          >
            <p className="text-sm text-text-secondary">{t('shipping.deleteCarrierConfirm', 'Are you sure you want to remove this carrier? This action cannot be undone.')}</p>
          </Modal>

          {/* Custom Carrier Creation Modal */}
          <Modal
            isOpen={showCustomCarrierModal}
            onClose={closeCustomCarrierModal}
            title={t('shipping.addCustomCarrier', 'Add Custom Carrier')}
            footer={
              <>
                <Button variant="secondary" onClick={closeCustomCarrierModal}>{t('common.cancel')}</Button>
                <Button
                  onClick={submitCustomCarrier}
                  isLoading={createCustomCarrierMutation.isPending}
                  disabled={!customCarrierName.trim()}
                >
                  {t('common.save')}
                </Button>
              </>
            }
          >
            <div className="space-y-4">
              <Input
                label={t('common.name')}
                required
                value={customCarrierName}
                onChange={(e) => setCustomCarrierName(e.target.value)}
                placeholder={t('shipping.carrierNamePlaceholder', 'e.g. My Express Delivery')}
              />

              {/* Logo upload */}
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1.5">
                  {t('shipping.carrierLogo', 'Logo')}
                </label>
                <div className="flex items-center gap-4">
                  {customCarrierLogo ? (
                    <div className="relative h-16 w-24 rounded-lg border border-border-default bg-bg-tertiary overflow-hidden p-1">
                      <img src={customCarrierLogo} alt="" className="h-full w-full object-contain" />
                      <button
                        type="button"
                        onClick={() => setCustomCarrierLogo('')}
                        className="absolute top-0.5 right-0.5 rounded-full bg-bg-primary/80 p-0.5 text-text-muted hover:text-danger transition-colors"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex h-16 w-24 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-border-default bg-bg-tertiary hover:border-accent/50 transition-colors">
                      <Upload className="h-5 w-5 text-text-muted" />
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleCustomCarrierLogoChange}
                      />
                    </label>
                  )}
                  <p className="text-xs text-text-muted">{t('shipping.logoHint', 'Upload a logo image (PNG, JPG)')}</p>
                </div>
              </div>

              <Input
                label={t('shipping.deliveryEstimate', 'Delivery Estimate')}
                value={customCarrierDelivery}
                onChange={(e) => setCustomCarrierDelivery(e.target.value)}
                placeholder={t('shipping.deliveryEstimatePlaceholder', 'e.g. 2-5 days')}
              />

              <Input
                label={t('shipping.baseShippingFee', 'Base Shipping Fee')}
                type="number"
                min="0"
                step="0.01"
                value={customCarrierFee || ''}
                onChange={(e) => setCustomCarrierFee(Number(e.target.value) || 0)}
              />
            </div>
          </Modal>

          {/* Carrier Pricing Modal */}
          <Modal
            isOpen={!!pricingCarrier}
            onClose={() => setPricingCarrier(null)}
            title={`${t('shipping.configurePricing', 'Configure Pricing')} - ${pricingCarrier?.title || pricingCarrier?.name}`}
            size="lg"
            footer={
              <>
                <Button variant="secondary" onClick={() => setPricingCarrier(null)}>{t('common.cancel')}</Button>
                <Button onClick={savePricing} isLoading={updateCarrierPricingMutation.isPending}>{t('common.save')}</Button>
              </>
            }
          >
            <div className="space-y-5">
              {/* Pricing mode */}
              <Select
                label={t('shipping.pricingMode', 'Pricing Mode')}
                options={[
                  { value: 'original', label: t('shipping.pricingMode_original', 'Original (carrier rate)') },
                  { value: 'deduction', label: t('shipping.pricingMode_deduction', 'Deduction (subtract from rate)') },
                  { value: 'fixed', label: t('shipping.pricingMode_fixed', 'Fixed price') },
                  { value: 'custom', label: t('shipping.pricingMode_custom', 'Cart-based rules') },
                ]}
                value={pricingMode}
                onChange={(e) => setPricingMode(e.target.value as CarrierPricingMode)}
              />

              {/* Mode-specific fields - shown based on selected mode */}
              {pricingMode === 'fixed' && (
                <Input label={t('shipping.fixedPrice', 'Fixed Shipping Price')} type="number" min="0" step="0.01" value={fixedPrice || ''} onChange={(e) => setFixedPrice(Number(e.target.value) || 0)} />
              )}

              {pricingMode === 'deduction' && (
                <Input label={t('shipping.priceDeduction', 'Deduction Amount')} type="number" min="0" step="0.01" value={priceDeduction || ''} onChange={(e) => setPriceDeduction(Number(e.target.value) || 0)} />
              )}

              {pricingMode === 'custom' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">{t('shipping.cartRules', 'Cart-Based Pricing Rules')}</label>
                    <Button size="sm" variant="secondary" onClick={addCartRule}>
                      <Plus className="h-3.5 w-3.5" />
                      {t('common.add')}
                    </Button>
                  </div>
                  {cartRules.map((rule, i) => (
                    <div key={i} className="rounded-lg border border-border-default p-3 space-y-3">
                      <div className="grid gap-3 sm:grid-cols-3">
                        <Input label={t('shipping.minCartValue', 'Min Cart')} type="number" min="0" step="0.01" value={rule.minCartValue || ''} onChange={(e) => updateCartRule(i, 'minCartValue', Number(e.target.value) || 0)} />
                        <Input label={t('shipping.maxCartValue', 'Max Cart')} type="number" min="0" step="0.01" value={rule.maxCartValue || ''} onChange={(e) => updateCartRule(i, 'maxCartValue', Number(e.target.value) || 0)} />
                        <Input label={t('shipping.shippingPrice', 'Shipping Price')} type="number" min="0" step="0.01" value={rule.shippingPrice || ''} onChange={(e) => updateCartRule(i, 'shippingPrice', Number(e.target.value) || 0)} />
                      </div>
                      <div className="flex items-center justify-between">
                        <Toggle label={t('shipping.freeShipping', 'Free Shipping')} checked={rule.freeShipping ?? false} onChange={(v) => updateCartRule(i, 'freeShipping', v)} />
                        <button onClick={() => removeCartRule(i)} className="text-text-muted hover:text-danger"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                  ))}
                  {cartRules.length === 0 && (
                    <p className="text-sm text-text-muted text-center py-4">{t('shipping.noCartRules', 'No rules. Add one above.')}</p>
                  )}
                </div>
              )}

              {/* Advanced options - collapsed by default */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowAdvancedPricing(!showAdvancedPricing)}
                  className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
                >
                  <ChevronDown className={`h-4 w-4 transition-transform ${showAdvancedPricing ? 'rotate-180' : ''}`} />
                  {t('shipping.advancedOptions', 'Advanced options')}
                </button>
                {showAdvancedPricing && (
                  <div className="mt-3 space-y-4">
                    {/* Fee & Discount */}
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Input label={t('shipping.fee', 'Additional Fee')} type="number" min="0" step="0.01" value={carrierFee || ''} onChange={(e) => setCarrierFee(Number(e.target.value) || 0)} />
                      <Input label={t('shipping.discount', 'Discount %')} type="number" min="0" max="100" step="1" value={carrierDiscount || ''} onChange={(e) => setCarrierDiscount(Number(e.target.value) || 0)} />
                    </div>
                    {carrierDiscount > 0 && (
                      <Input label={t('shipping.discountMinOrder', 'Discount Min. Order Amount')} type="number" min="0" step="0.01" value={discountMinOrder || ''} onChange={(e) => setDiscountMinOrder(Number(e.target.value) || 0)} />
                    )}
                    {/* Free shipping threshold */}
                    <Input label={t('shippingRates.freeThreshold')} type="number" min="0" step="0.01" value={freeThreshold || ''} onChange={(e) => setFreeThreshold(Number(e.target.value) || 0)} />
                    {/* Show original price */}
                    <Toggle
                      label={t('shipping.showOriginalPrice', 'Show original price (strikethrough)')}
                      checked={showOriginalPrice}
                      onChange={setShowOriginalPrice}
                    />
                  </div>
                )}
              </div>
            </div>
          </Modal>
        </>
      )}

      {/* ── Addresses Tab ── */}
      {activeTab === 'addresses' && (
        <>
          <div className="mb-4 flex justify-end">
            <PermissionGate requires="shipping.any">
              {/* Single warehouse/pickup address: hide "add" once one exists. */}
              {addresses.length === 0 && (
                <Button onClick={openAddAddress}>
                  <Plus className="h-4 w-4" />
                  {t('shipping.addAddress')}
                </Button>
              )}
            </PermissionGate>
          </div>
          {addresses.length === 0 ? (
            <EmptyState title={t('common.noData')} />
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {addresses.map((addr) => (
                <div key={addr._id} className="rounded-xl border border-border-default bg-bg-secondary p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium">{addr.name}</h3>
                      {addr.address && <p className="text-sm text-text-secondary mt-1">{addr.address}</p>}
                      {(addr.city || addr.country) && (
                        <p className="text-sm text-text-muted">{[addr.city, addr.country].filter(Boolean).join(', ')}</p>
                      )}
                      {addr.phone && <p className="text-sm text-text-muted">{addr.phone}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      {addr.isDefault && <Badge variant="accent">{t('settings.default')}</Badge>}
                      <PermissionGate requires="shipping.any">
                        <button
                          onClick={() => openEditAddress(addr)}
                          className="rounded-lg p-1.5 text-text-muted hover:bg-accent/10 hover:text-accent transition-colors"
                          title={t('common.edit', 'Edit')}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteAddressMutation.mutate(addr._id)}
                          className="rounded-lg p-1.5 text-text-muted hover:bg-danger/10 hover:text-danger transition-colors"
                          title={t('common.delete', 'Delete')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </PermissionGate>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Modal
            isOpen={showAddressModal}
            onClose={closeAddressModal}
            title={editingAddress ? t('shipping.editAddress', 'Edit Address') : t('shipping.addAddress')}
            footer={
              <>
                <Button variant="secondary" onClick={closeAddressModal}>{t('common.cancel')}</Button>
                <Button
                  onClick={submitAddress}
                  isLoading={addAddressMutation.isPending}
                  disabled={!canSaveAddress}
                >
                  {t('common.save')}
                </Button>
              </>
            }
          >
            <div className="space-y-4">
              <Input label={t('common.name')} required value={addrName} onChange={(e) => setAddrName(e.target.value)} />
              <Input label={t('common.phone')} value={addrPhone} onChange={(e) => setAddrPhone(e.target.value)} />

              {editingAddress && editingAddress.address && (
                <p className="rounded-lg bg-bg-field px-3 py-2 text-xs text-text-muted">
                  {t('shipping.currentLocation', 'Current location')}: {editingAddress.address}
                  <span className="block mt-1">{t('shipping.reselectToChange', 'Re-select country then city below to change it.')}</span>
                </p>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                {/* Country */}
                <div>
                  <label className="block text-sm text-text-secondary mb-1.5">{t('shipping.country', 'Country')}</label>
                  <select
                    className="w-full rounded-lg border border-border-default bg-bg-field px-3 py-2.5 text-sm text-text-primary outline-none focus:border-accent"
                    value={addrCountry ?? ''}
                    onChange={(e) => {
                      const v = e.target.value ? Number(e.target.value) : null;
                      setAddrCountry(v);
                      setAddrRegion(null);
                      setAddrCity(null);
                      setAddrDistrict(null);
                    }}
                  >
                    <option value="">{t('shipping.selectCountry', 'Select country...')}</option>
                    {(addrCountries ?? []).map((c) => (
                      <option key={torodId(c, 'country') ?? ''} value={torodId(c, 'country') ?? ''}>{torodLabel(c, i18n.language)}</option>
                    ))}
                  </select>
                </div>

                {/* Region */}
                <div>
                  <label className="block text-sm text-text-secondary mb-1.5">{t('shipping.region', 'Region')}</label>
                  <select
                    className="w-full rounded-lg border border-border-default bg-bg-field px-3 py-2.5 text-sm text-text-primary outline-none focus:border-accent disabled:opacity-50"
                    value={addrRegion ?? ''}
                    onChange={(e) => {
                      const v = e.target.value ? Number(e.target.value) : null;
                      setAddrRegion(v);
                      setAddrCity(null);
                      setAddrDistrict(null);
                    }}
                    disabled={!addrCountry}
                  >
                    <option value="">{t('shipping.selectRegion', 'Select region...')}</option>
                    {(addrRegions ?? []).map((r) => (
                      <option key={torodId(r, 'region') ?? ''} value={torodId(r, 'region') ?? ''}>{torodLabel(r, i18n.language)}</option>
                    ))}
                  </select>
                </div>

                {/* City */}
                <div>
                  <label className="block text-sm text-text-secondary mb-1.5">{t('shipping.city', 'City')}</label>
                  <select
                    className="w-full rounded-lg border border-border-default bg-bg-field px-3 py-2.5 text-sm text-text-primary outline-none focus:border-accent disabled:opacity-50"
                    value={addrCity ?? ''}
                    onChange={(e) => {
                      const v = e.target.value ? Number(e.target.value) : null;
                      setAddrCity(v);
                      setAddrDistrict(null);
                    }}
                    disabled={!addrRegion}
                  >
                    <option value="">{t('shipping.selectCity', 'Select city...')}</option>
                    {(addrCities ?? []).map((c) => (
                      <option key={torodId(c, 'city') ?? ''} value={torodId(c, 'city') ?? ''}>{torodLabel(c, i18n.language)}</option>
                    ))}
                  </select>
                </div>

                {/* District */}
                <div>
                  <label className="block text-sm text-text-secondary mb-1.5">{t('shipping.district', 'District')}</label>
                  <select
                    className="w-full rounded-lg border border-border-default bg-bg-field px-3 py-2.5 text-sm text-text-primary outline-none focus:border-accent disabled:opacity-50"
                    value={addrDistrict ?? ''}
                    onChange={(e) => setAddrDistrict(e.target.value ? Number(e.target.value) : null)}
                    disabled={!addrCity}
                  >
                    <option value="">{t('shipping.selectDistrict', 'Select district...')}</option>
                    {(addrDistricts ?? []).map((d) => (
                      <option key={torodId(d, 'district') ?? ''} value={torodId(d, 'district') ?? ''}>{torodLabel(d, i18n.language)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <Input
                label={t('shipping.streetDetails', 'Street / building details (optional)')}
                value={addrStreet}
                onChange={(e) => setAddrStreet(e.target.value)}
              />
            </div>
          </Modal>
        </>
      )}

      {/* ── Shipping Labels Section ── */}
      <div className="mt-8 rounded-xl border border-border-default bg-bg-secondary p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent">
            <Printer className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-medium">{t('shippingLabels.title', 'Shipping Labels')}</h3>
            <p className="text-xs text-text-muted">{t('shippingLabels.desc', 'Generate and print shipping labels for orders.')}</p>
          </div>
        </div>
        <PermissionGate requires="shipping.any">
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label={t('orders.orderId')}
                value={labelOrderId}
                onChange={(e) => setLabelOrderId(e.target.value)}
                placeholder={t('shippingLabels.orderIdPlaceholder', 'Enter order ID...')}
              />
              <Select
                label={t('shippingLabels.selectCarrier', 'Select Carrier')}
                options={[
                  { value: '', label: t('shippingLabels.selectCarrierPlaceholder', 'Select a carrier...') },
                  // Only Torod carriers can generate labels; their numeric Torod id
                  // (carrierId/refId) is what the label endpoint expects.
                  ...carriers
                    .filter(c => c.isActive && c.gateway !== 'custom')
                    .map(c => ({
                      value: String(c.carrierId || c.refId || ''),
                      label: c.title || c.arName || c.name,
                    })),
                ]}
                value={labelCarrierId}
                onChange={(e) => setLabelCarrierId(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => createLabelMutation.mutate({ orderId: labelOrderId.trim(), carrierId: labelCarrierId })}
                isLoading={createLabelMutation.isPending}
                disabled={!labelOrderId.trim() || !labelCarrierId}
              >
                <Printer className="h-4 w-4" />
                {t('shippingLabels.create', 'Create Label')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => labelOrderId && printLabelMutation.mutate(labelOrderId)}
                isLoading={printLabelMutation.isPending}
                disabled={!labelOrderId.trim()}
              >
                <Printer className="h-4 w-4" />
                {t('shippingLabels.print', 'Print Existing')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => labelOrderId && downloadLabelMutation.mutate(labelOrderId)}
                isLoading={downloadLabelMutation.isPending}
                disabled={!labelOrderId.trim()}
              >
                <Download className="h-4 w-4" />
                {t('shippingRates.downloadPdf', 'Download PDF')}
              </Button>
            </div>
          </div>
        </PermissionGate>
      </div>
    </div>
  );
}
